<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex items-center justify-between space-x-2">
            <h1 class="text-2xl font-semibold text-gray-700">Broadcasts</h1>
            <div class="flex items-center space-x-2">
                <UButton to="/broadcasts/templates" color="white" icon="i-ph-file-text">
                    Templates
                </UButton>
                <UButton to="/broadcasts/segments" color="white" icon="i-ph-users-three">
                    Manage Segments
                </UButton>
                <UButton to="/broadcasts/suppressions" color="white" icon="i-ph-prohibit">
                    Suppressions
                </UButton>
                <UButton to="/broadcasts/rates" color="white" icon="i-ph-currency-dollar">
                    Rates
                </UButton>
                <UButton @click="overlays.create = true" color="black" icon="i-ph-plus-bold">
                    New Broadcast
                </UButton>
            </div>
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="broadcasts"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-megaphone-duotone', label: 'No broadcasts.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', td: { padding: 'py-3 whitespace-pre-wrap' } }"
                class="h-full"
            >
                <template #number-data="{ row }">
                    <span class="font-mono">
                        {{ row.number.toString().padStart(8, '0') }}
                    </span>
                </template>
                <template #title-data="{ row }">
                    <div class="w-56 xl:w-72">
                        <p class="line-clamp-3">{{ row.title }}</p>
                    </div>
                </template>
                <template #channel-data="{ row }">
                    <UBadge :label="channelLabel(row.template?.channel)" :color="channelColor(row.template?.channel)" size="sm" variant="subtle" />
                </template>
                <template #segment-data="{ row }">
                    <span class="text-sm text-gray-600">{{ row.segment?.name ?? '–' }}</span>
                </template>
                <template #status-data="{ row }">
                    <UBadge :label="row.status" :color="statusColor(row.status)" size="sm" variant="subtle" />
                </template>
                <template #recipients-data="{ row }">
                    <span class="text-sm tabular-nums">{{ row.estimated_recipients ?? '–' }}</span>
                </template>
                <template #cost-data="{ row }">
                    <span class="text-sm tabular-nums">{{ row.estimated_cost != null ? `$${Number(row.estimated_cost).toFixed(2)}` : '–' }}</span>
                </template>
                <template #time-data="{ row }">
                    <span class="text-sm text-gray-500 whitespace-nowrap">
                        {{ formatTime(row) }}
                    </span>
                </template>
                <template #actions-data="{ row }">
                    <UDropdown :items="actions(row)">
                        <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                    </UDropdown>
                </template>
            </UTable>
        </div>

        <TablePagination :pagination="pagination" class="shrink-0" />

        <OverlayBroadcastCreateBroadcast v-model:open="overlays.create" @reload="fetchData" />
        <OverlayBroadcastEditBroadcast v-model:open="overlays.edit" @reload="fetchData" :broadcast="selectedBroadcast" />
        <OverlayBroadcastRemoveBroadcast v-model:open="overlays.remove" @reload="fetchData" :broadcast="selectedBroadcast" />
        <OverlayBroadcastTestBroadcast v-model:open="overlays.test" :broadcast="selectedBroadcast" />
        <OverlayBroadcastPublishBroadcast v-model:open="overlays.publish" @reload="fetchData" :broadcast="selectedBroadcast" />
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'
import { watchDebounced } from '@vueuse/core'
import type { Broadcast } from '~/types/broadcast'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })
useHead({ title: 'Broadcasts' })

const client = useSupabaseClient()
const router = useRouter()
const pagination = usePagination()

const loading = ref(false)
const broadcasts = ref<Broadcast[]>([])
const selectedBroadcast = ref<Broadcast>()

const overlays = reactive({
    create: false,
    edit: false,
    remove: false,
    test: false,
    publish: false,
})

const columns = [
    { key: 'number', label: 'ID' },
    { key: 'title', label: 'Title' },
    { key: 'channel', label: 'Channel' },
    { key: 'segment', label: 'Segment' },
    { key: 'status', label: 'Status' },
    { key: 'recipients', label: 'Recipients' },
    { key: 'cost', label: 'Cost' },
    { key: 'time', label: 'Time' },
    { key: 'actions', class: 'w-px' },
]

function channelLabel(channel?: string | null) {
    const labels: Record<string, string> = { email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp', push: 'Push' }
    return labels[channel ?? ''] ?? (channel ?? '–')
}

function channelColor(channel?: string | null) {
    const colors: Record<string, string> = { email: 'blue', sms: 'violet', whatsapp: 'green', push: 'orange' }
    return colors[channel ?? ''] ?? 'gray'
}

function statusColor(status: string) {
    const colors: Record<string, string> = {
        draft: 'gray',
        scheduled: 'amber',
        sending: 'blue',
        sent: 'green',
        failed: 'red',
        canceled: 'orange',
    }
    return colors[status] ?? 'gray'
}

function formatTime(row: Broadcast) {
    const t = row.sent_at ?? row.scheduled_at ?? row.updated_at
    if (!t) return '–'
    return new Date(t).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
}

const actions = (broadcast: Broadcast) => {
    const status = broadcast.status

    if (status === 'draft') {
        return [
            [
                {
                    label: 'Edit',
                    icon: 'i-ph-pencil-fill',
                    click: () => { selectedBroadcast.value = broadcast; overlays.edit = true },
                },
                {
                    label: 'Test',
                    icon: 'i-ph-test-tube-fill',
                    iconClass: 'text-amber-400',
                    click: () => { selectedBroadcast.value = broadcast; overlays.test = true },
                },
                {
                    label: 'Publish',
                    icon: 'i-ph-paper-plane-tilt-fill',
                    iconClass: 'text-green-400',
                    click: () => { selectedBroadcast.value = broadcast; overlays.publish = true },
                },
            ],
            [
                {
                    label: 'Delete',
                    icon: 'i-ph-trash-fill',
                    iconClass: 'text-red-400',
                    click: () => { selectedBroadcast.value = broadcast; overlays.remove = true },
                },
            ],
        ]
    }

    if (status === 'scheduled') {
        return [
            [
                {
                    label: 'Edit',
                    icon: 'i-ph-pencil-fill',
                    click: () => { selectedBroadcast.value = broadcast; overlays.edit = true },
                },
                {
                    label: 'Cancel',
                    icon: 'i-ph-x-circle-fill',
                    iconClass: 'text-orange-400',
                    click: () => cancelBroadcast(broadcast),
                },
            ],
            [
                {
                    label: 'Delete',
                    icon: 'i-ph-trash-fill',
                    iconClass: 'text-red-400',
                    click: () => { selectedBroadcast.value = broadcast; overlays.remove = true },
                },
            ],
        ]
    }

    if (status === 'sending' || status === 'sent' || status === 'failed') {
        return [
            [
                {
                    label: 'View report',
                    icon: 'i-ph-chart-bar-fill',
                    click: () => router.push(`/broadcasts/${broadcast.id}`),
                },
            ],
        ]
    }

    return []
}

async function cancelBroadcast(broadcast: Broadcast) {
    await client.from('broadcasts').update({ status: 'canceled' } as never).eq('id', broadcast.id)
    fetchData()
}

onMounted(() => fetchData())
watchDebounced([], () => fetchData(), { debounce: 600 })
watch(() => pagination.from, () => fetchData())

async function fetchData() {
    loading.value = true
    broadcasts.value = []
    pagination.setTotal(undefined)

    const { count, data } = await client.from('broadcasts')
        .select(`id, number, title, template:templates(channel), status, segment:broadcast_segments(name), estimated_recipients, estimated_cost, totals, scheduled_at, sent_at, updated_at`, { count: 'exact' })
        .order('number', { ascending: false })
        .range(pagination.from, pagination.to)
        .returns<Broadcast[]>()

    if (count != null && data) {
        broadcasts.value = data
        pagination.setTotal(count)
    }

    loading.value = false
}
</script>
